import { useEffect, useMemo, useRef } from 'react';

import { ToastContext } from './toast-context';

import type { ToastWrapperProps } from './toaster.types';

export const ToastWrapper = (props: ToastWrapperProps): JSX.Element => {
  const { children, className, onHeightUpdate, style, toast } = props;

  const { createdAt, duration, id, visible } = toast;

  const ref = useRef<HTMLDivElement | null>(null);

  /**
   * This is used to calculate the vertical offset when there are multiple toasts.
   * The offset will be used to translate the toast vertically.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const updateHeight = (): void => {
      const height = el.getBoundingClientRect().height;
      onHeightUpdate(id, height);
    };

    updateHeight();

    const observer = new MutationObserver(updateHeight);

    observer.observe(el, {
      characterData: true,
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [id, onHeightUpdate]);

  const toastContextValue = useMemo(() => ({ createdAt, duration, id }), [id, createdAt, duration]);

  return (
    <ToastContext.Provider value={toastContextValue}>
      <div className={className} ref={ref} style={style}>
        <div className={visible ? 'rbui-toast-animation--visible' : 'rbui-toast-animation--hidden'}>{children}</div>
      </div>
    </ToastContext.Provider>
  );
};
